import { isReminderEvent } from "@/components/calendars/canEditEvent.ts";
import {
  type EventDetailCloseGuard,
  EventDetailPanel,
} from "@/components/calendars/EventDetailPanel.tsx";
import { ReminderDetailPanel } from "@/components/calendars/ReminderDetailPanel.tsx";
import { eventPendingDeleteAtom } from "@/components/calendars/eventDeleteAtom.ts";
import {
  useEventFromCache,
  useEventPanelTimeKey,
} from "@/components/calendars/eventDetailHooks.ts";
import { useSelectedEventEnrichment } from "@/components/calendars/useSelectedEventEnrichment.tsx";
import { eventDetailPath } from "@/components/calendars/calendar-url.ts";
import { useCanvasActionContext } from "@/components/canvas/canvasContext.tsx";
import { toUrnString } from "@/types/urn.ts";
import { useSetAtom } from "jotai";
import { useCallback, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "wouter";
import { AgentPopover } from "./AgentPopover.tsx";
import { AgentPromptInput } from "@/components/messages/Omnibox/AgentPromptInput.tsx";

interface EventClickPopoverProps {
  /** The event the user clicked, identified by FullCalendar's gcalId. */
  eventId: string;
  /** Rect of the clicked event chip, captured at click time. The popover
   *  anchors against this fixed rect so the position is stable even if
   *  FullCalendar re-renders the chip during a mutation. */
  anchorRect: DOMRect;
  /** Called when the popover is dismissed (X / Escape / outside-click /
   *  successful prompt submit / expanding to the fuller surface). */
  onDismiss: () => void;
}

/**
 * Trigger 1 — opens when the user clicks an event chip on the grid.
 *
 * Embeds the full `EventDetailPanel` (the same rich detail/edit UI the canvas
 * card and the event page use) inside the anchored popover: scrollable content
 * with the agent prompt input pinned at the bottom, and the panel's own header
 * carrying the expand affordance. Reusing the panel keeps one source of truth
 * for recurrence, conferencing, location (conference labels stripped),
 * participants, RSVP (with recurring-scope), reminders, and delete/cancel —
 * rather than a second hand-built layout.
 */
export const EventClickPopover = ({ eventId, anchorRect, onDismiss }: EventClickPopoverProps) => {
  const event = useEventFromCache(eventId);
  const canvas = useCanvasActionContext();
  const [, setLocation] = useLocation();

  // Freeze the click-time rect so the popover doesn't jump if FullCalendar
  // re-renders the chip during a mutation (optimistic write, RSVP, etc.).
  const virtualAnchor = useMemo(() => ({ getBoundingClientRect: () => anchorRect }), [anchorRect]);

  // Published by EventDetailPanel — routes dismisses through its unsaved-changes
  // prompt. Falls back to closing directly if the panel hasn't registered yet.
  const closeGuardRef = useRef<EventDetailCloseGuard | null>(null);
  const guardedClose = useCallback((proceed: () => void) => {
    if (closeGuardRef.current) closeGuardRef.current(proceed);
    else proceed();
  }, []);

  // Hand the event to the app-level confirmation and dismiss. Not rendered
  // here: this popover unmounts on dismiss, so a confirmation inside it would
  // never survive its own trigger (BIG-8266).
  const setEventPendingDelete = useSetAtom(eventPendingDeleteAtom);
  const startDelete = useCallback(() => {
    // Through the guard, and the atom set only once the close is agreed to: a
    // dirty form would otherwise be discarded on the way to the confirmation,
    // and cancelling that confirmation would leave the edit gone. Backing out
    // of the unsaved-changes prompt now leaves everything as it was.
    guardedClose(() => {
      if (event) setEventPendingDelete(event);
      onDismiss();
    });
  }, [guardedClose, setEventPendingDelete, event, onDismiss]);

  // The popover is always open while mounted — Calendar.tsx unmounts it when
  // dismissed, so we just forward Radix's close signal through the guard.
  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) guardedClose(onDismiss);
    },
    [guardedClose, onDismiss],
  );

  // Promote the anchored popover to the canvas card — the fuller surface for the
  // same event. Falls back to the event's own page where there is no canvas to
  // present into (mobile, gate-off), so the affordance is never a dead click.
  const handlePromoteToCanvas = useCallback(() => {
    guardedClose(() => {
      onDismiss();
      const urn = toUrnString({ type: "source", id: eventId });
      if (canvas?.resetTo(urn)) return;
      setLocation(eventDetailPath(eventId));
    });
  }, [canvas, eventId, guardedClose, onDismiss, setLocation]);

  // Mirror the panel's dirty state. While editing we render a transparent
  // backdrop (below the popover, above the calendar) so an outside click is
  // absorbed there instead of acting on the calendar — Radix still sees it as
  // "outside" and routes to the unsaved-changes guard. A separate portal (vs.
  // toggling the popover's `modal`) avoids remounting/reverting the edits.
  const [isDirty, setIsDirty] = useState(false);
  const handleDirtyChange = useCallback((dirty: boolean) => setIsDirty(dirty), []);

  // Cancelling a dirty edit in EventDetailPanel discards changes by remounting
  // it. Dirty clears in the same batch: waiting for the fresh panel's
  // onDirtyChange(false) would unfreeze timeKey a render later and trigger a
  // second remount when the time changed during the edit.
  const [resetKey, setResetKey] = useState(0);
  const handleCancelEdit = useCallback(() => {
    setIsDirty(false);
    setResetKey((k) => k + 1);
  }, []);

  // Pristine → remount on an external reschedule; dirty → key frozen so the
  // draft survives (see useEventPanelTimeKey).
  const timeKey = useEventPanelTimeKey(event, isDirty);

  // Register the `selectedEvent` enrichment so the agent prompt carries the
  // event handle to the server, which expands the full event details into the
  // actor's agentContent at send time.
  useSelectedEventEnrichment(
    event?.urn
      ? {
          urn: event.urn,
          eventId,
          summary: event.summary,
          startDateTime: event.start.dateTime,
          endDateTime: event.end.dateTime,
        }
      : null,
  );

  if (!event) {
    // No cached event — render nothing rather than a half-state popover.
    return null;
  }

  return (
    <>
      {/* While editing, a transparent backdrop absorbs outside clicks (below the
          popover, above the calendar) so they reach the unsaved-changes guard
          instead of acting on the grid. */}
      {isDirty &&
        createPortal(
          <div
            aria-hidden
            className="fixed inset-0 z-40"
            data-statsig-name="event-popover-backdrop"
          />,
          document.body,
        )}
      <AgentPopover
        open
        onOpenChange={handleOpenChange}
        anchor={virtualAnchor}
        ariaLabel={`Event: ${event.summary}`}
        onInteractOutside={(e) => {
          const target = e.target as Element | null;
          // Always keep the popover open while interacting with the embedded
          // panel's portaled UI (Select/recurrence dropdowns, the delete /
          // recurring-scope / unsaved-changes dialogs) — those render outside the
          // popover's DOM.
          if (
            target?.closest?.(
              "[data-radix-popper-content-wrapper],[role='menu'],[role='listbox'],[role='dialog'],[role='alertdialog']",
            )
          ) {
            e.preventDefault();
            return;
          }
          // Clicking another event chip reanchors the popover to it rather than
          // dismissing. While dirty the backdrop sits over the chips, so the
          // target is the backdrop (not .fc-event) and this falls through to the
          // unsaved-changes guard instead.
          if (target?.closest?.(".fc-event")) {
            e.preventDefault();
          }
        }}
        side="right"
        widthPx={440}
        // Drop the default padding (the panel owns its own) and cap the height so
        // the panel's content region scrolls while the prompt input stays pinned.
        bodyClassName="p-0 overflow-hidden flex flex-col max-h-[min(680px,80vh)]"
      >
        {/* Each panel provides its own header and a scrollable content
            region; the expand affordance lives in that header, which is why
            both branches are handed `onExpand`. */}
        <div className="flex-1 min-h-0 flex flex-col">
          {/* Clicking a chip on the grid is the main way anyone opens a
              reminder, so this branch has to be here as well as in the sidebar
              — the event panel shows a dozen fields a reminder has no meaning
              for. See ReminderDetailPanel. */}
          {isReminderEvent(event) ? (
            <ReminderDetailPanel
              key={`${event.gcalId}-${resetKey}-${timeKey}`}
              event={event}
              onClose={onDismiss}
              onExpand={handlePromoteToCanvas}
            />
          ) : (
            <EventDetailPanel
              key={`${event.gcalId}-${resetKey}-${timeKey}`}
              event={event}
              onClose={onDismiss}
              onCancel={handleCancelEdit}
              closeGuardRef={closeGuardRef}
              onDirtyChange={handleDirtyChange}
              onExpand={handlePromoteToCanvas}
              // A link out of the panel leaves this popover anchored to a chip
              // on a calendar the user is no longer looking at, so it steps
              // aside first — the same handoff "Open in sidebar" makes.
              onNavigateAway={onDismiss}
              onRequestDelete={startDelete}
            />
          )}
        </div>

        {/* Pinned footer — the agent prompt handoff. */}
        <div className="shrink-0 border-t border-border p-3">
          <AgentPromptInput
            placeholder="Edit event or ask Okthx…"
            onEscape={onDismiss}
            onSubmitted={onDismiss}
          />
        </div>
      </AgentPopover>
    </>
  );
};
